import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Container,
  TextField,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  InputAdornment,
  Fade,
  Zoom,
  alpha,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton
} from '@mui/material';
import HomeFooter from './HomeFooter';
import Navbar from './Navbar';
import SendIcon from '@mui/icons-material/Send';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import CloudIcon from '@mui/icons-material/Cloud';
import SecurityIcon from '@mui/icons-material/Security';
import GroupsIcon from '@mui/icons-material/Groups';
import ComputerIcon from '@mui/icons-material/Computer';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import EmailIcon from '@mui/icons-material/Email';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DownloadIcon from '@mui/icons-material/Download';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import FacebookIcon from '@mui/icons-material/Facebook';
import NotificationsIcon from '@mui/icons-material/Notifications';


const Newsroom = () => {

  const [email, setEmail] = useState('');
  const [expandedArticle, setExpandedArticle] = useState(null);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const bgImage = `${process.env.PUBLIC_URL}/images/bg77.jpg`;
  // Modern technology color scheme
  const colors = {
    primary: '#0066cc',
    secondary: '#00a86b',
    accent: '#ff6b35',
    darkBlue: '#003366',
    lightGray: '#f8f9fa',
    mediumGray: '#e9ecef',
    darkGray: '#495057',
    textLight: '#ffffff',
    textDark: '#212529',
    white: '#ffffff',
    black: '#000000'
  };

  // News articles and updates
  const articles = [
    {
      id: 1,
      title: "AI-Driven Business Intelligence: The Future of Data Analytics",
      shortContent: "How artificial intelligence is revolutionizing business analytics and decision-making processes across industries.",
      detailedContent: [
        "✅ Real-time predictive analytics using machine learning algorithms",
        "✅ Automated report generation with natural language processing",
        "✅ Integration with existing ERP systems for unified data views",
        "✅ Custom dashboard creation with drag-and-drop functionality",
        "✅ Anomaly detection and automated alert systems",
        "✅ Mobile-first analytics for on-the-go decision making",
        "✅ Cross-platform data visualization tools",
        "✅ Compliance and security monitoring built-in"
      ],
      benefits: [
        "Reduce data analysis time by 70%",
        "Improve decision accuracy by 45%",
        "Cut operational costs by 30%",
        "Increase team productivity by 60%"
      ],
      category: "Technology",
      readTime: "5 min read",
      author: "Sarah Chen, Chief Technology Officer",
      icon: <AnalyticsIcon />,
      bgColor: '#e8f4f8',
      textColor: colors.textDark
    },
    {
      id: 2,
      title: "Hybrid Work Solutions: Secure Remote Access Infrastructure",
      shortContent: "Building secure and scalable remote work infrastructure for modern enterprises.",
      detailedContent: [
        "✅ Zero-trust security architecture implementation",
        "✅ VPN-less secure access to corporate resources",
        "✅ Multi-factor authentication across all platforms",
        "✅ Device management and security compliance",
        "✅ Real-time collaboration tools integration",
        "✅ Network performance optimization",
        "✅ Data loss prevention systems",
        "✅ 24/7 security monitoring and incident response"
      ],
      benefits: [
        "99.9% system uptime guarantee",
        "50% reduction in security incidents",
        "40% improvement in remote team productivity",
        "70% faster onboarding for remote employees"
      ],
      category: "Security",
      readTime: "4 min read",
      author: "Marcus Johnson, Security Director",
      icon: <SecurityIcon />,
      bgColor: '#f0f8e8',
      textColor: colors.textDark
    },
    {
      id: 3,
      title: "Cloud Migration Strategies for Legacy Systems",
      shortContent: "Step-by-step guide to migrating traditional systems to cloud infrastructure.",
      detailedContent: [
        "✅ Comprehensive system assessment and planning",
        "✅ Phased migration approach with minimal downtime",
        "✅ Data integrity verification and validation",
        "✅ Performance optimization in cloud environment",
        "✅ Cost management and optimization strategies",
        "✅ Disaster recovery and backup solutions",
        "✅ Staff training and change management",
        "✅ Ongoing maintenance and support framework"
      ],
      benefits: [
        "Reduce IT infrastructure costs by 40-60%",
        "Improve system scalability and flexibility",
        "Enhance disaster recovery capabilities",
        "Increase overall system performance by 35%"
      ],
      category: "Cloud",
      readTime: "6 min read",
      author: "David Park, Cloud Architect",
      icon: <CloudIcon />,
      bgColor: '#f8f0e8',
      textColor: colors.textDark
    },
    {
      id: 4,
      title: "Digital Transformation in Manufacturing",
      shortContent: "How Industry 4.0 technologies are reshaping manufacturing processes.",
      detailedContent: [
        "✅ IoT sensor integration for real-time monitoring",
        "✅ Predictive maintenance using AI algorithms",
        "✅ Supply chain optimization with blockchain",
        "✅ Digital twin technology implementation",
        "✅ Automated quality control systems",
        "✅ Energy consumption optimization",
        "✅ Workforce upskilling programs",
        "✅ Sustainable manufacturing practices"
      ],
      benefits: [
        "Increase production efficiency by 30%",
        "Reduce maintenance costs by 25%",
        "Improve product quality by 40%",
        "Decrease energy consumption by 20%"
      ],
      category: "Industry 4.0",
      readTime: "7 min read",
      author: "Lisa Rodriguez, Industry Consultant",
      icon: <BusinessCenterIcon />,
      bgColor: '#f8e8f0',
      textColor: colors.textDark
    },
    {
      id: 5,
      title: "Cybersecurity Compliance for Financial Institutions",
      shortContent: "Navigating regulatory requirements and implementing robust security measures.",
      detailedContent: [
        "✅ Regulatory compliance frameworks (GDPR, CCPA, PCI-DSS)",
        "✅ Automated compliance reporting systems",
        "✅ Threat intelligence and monitoring platforms",
        "✅ Incident response and recovery planning",
        "✅ Employee security awareness training",
        "✅ Third-party vendor risk management",
        "✅ Data encryption and privacy measures",
        "✅ Regular security audits and assessments"
      ],
      benefits: [
        "100% regulatory compliance achievement",
        "70% reduction in audit preparation time",
        "Enhanced customer trust and confidence",
        "Significant reduction in compliance costs"
      ],
      category: "Compliance",
 
      readTime: "5 min read",
      author: "Robert Kim, Compliance Officer",
      icon: <GroupsIcon />,
      bgColor: '#e8f0f8',
      textColor: colors.textDark
    },
    {
      id: 6,
      title: "Future of Work: Collaborative Technology Stack",
      shortContent: "Building the perfect technology stack for modern collaborative environments.",
      detailedContent: [
        "✅ Unified communication platforms integration",
        "✅ Project management and workflow automation",
        "✅ Virtual and augmented reality collaboration",
        "✅ Knowledge management and sharing systems",
        "✅ Employee engagement and feedback tools",
        "✅ Learning management system integration",
        "✅ Performance analytics and insights",
        "✅ Customizable workspace configurations"
      ],
      benefits: [
        "Improve team collaboration by 50%",
        "Reduce meeting times by 30%",
        "Increase project completion rate by 40%",
        "Boost employee satisfaction by 35%"
      ],
      category: "Workplace",
     
      readTime: "4 min read",
      author: "Alex Morgan, Workplace Strategist",
      icon: <ComputerIcon />,
      bgColor: '#f8f8e8',
      textColor: colors.textDark
    },
  ];

  const stats = [
    { value: "500+", label: "Enterprise Clients", icon: "🏢" },
    { value: "99.5%", label: "Uptime SLA", icon: "📊" },
    { value: "24/7", label: "Global Support", icon: "🌎" },
    { value: "50+", label: "Countries Served", icon: "📍" },
  ];

  const resources = [
    { title: "2024 Tech Trends Report", type: "PDF Report", icon: "📄" },
    { title: "Cloud Migration Webinar", type: "Video", icon: "🎥" },
    { title: "Security Whitepaper", type: "Research", icon: "📑" },
    { title: "Case Studies", type: "Collection", icon: "📚" },
  ];

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email) {
      alert(`Thank you for subscribing to our Newsroom: ${email}`);
      setEmail('');
    }
  };

  const handleLearnMoreClick = (articleId) => {
    if (expandedArticle === articleId) {
      setExpandedArticle(null);
    } else {
      setExpandedArticle(articleId);
    }
  };

  const handleAccordionChange = (index) => {
    if (expandedIndex === index) {
      setExpandedIndex(null);
    } else {
      setExpandedIndex(index);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: colors.lightGray,
      color: colors.textDark,
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif'
    }}>
      <Navbar />

      {/* Hero Banner - Newsroom */}
      <Box sx={{
        width: '100%',
        backgroundImage: `url(${bgImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        color: '#1e293b',
        py: { xs: 10, md: 12 },
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: '#ffffff',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          zIndex: 0
        }
      }}>

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <Grid container alignItems="center" spacing={4}>
            <Grid item xs={12} md={7}>
              <Box sx={{
                position: 'relative',
                zIndex: 1
              }}>
                {/* Chip */}
                <Box sx={{
                  display: 'inline-block',
                  position: 'relative',
                  marginBottom: '24px'
                }}>
                  <Chip
                    label="OFFICIAL NEWSROOM"
                    sx={{
                      bgcolor: '#e6f0ff',
                      color: '#2563eb',
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      px: 3,
                      py: 1,
                      letterSpacing: 1.5,
                      border: '1px solid #2563eb',
                      position: 'relative',
                      zIndex: 1
                    }}
                  />
                </Box>

                <Typography variant="h1" sx={{
                  fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                  fontWeight: 900,
                  lineHeight: 1.1,
                  mb: 3,
                  color: '#1e293b',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '-12px',
                    left: 0,
                    width: '120px',
                    height: '4px',
                    backgroundColor: '#2563eb'
                  }
                }}>
                  Technology Insights
                  <Box component="span" sx={{
                    color: '#2563eb',
                    display: 'block',
                    fontWeight: 800
                  }}>
                    For Modern Enterprises
                  </Box>
                </Typography>

                <Typography variant="h5" sx={{
                  mb: 4,
                  fontWeight: 300,
                  color: '#475569',
                  maxWidth: '600px',
                  lineHeight: 1.6,
                  position: 'relative',
                  pl: 3,
                  borderLeft: '4px solid #2563eb'
                }}>
                  Latest research industry trends and technology solutions for forward-thinking business.
                </Typography>

                {/* Stats */}
                <Box sx={{ display: 'flex', gap: 4, mt: 4 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2563eb' }}>500+</Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>Articles Published</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2563eb' }}>50K+</Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>Monthly Readers</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2563eb' }}>100+</Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>Industry Experts</Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* <Grid item xs={12} md={5}>
              <Box sx={{
                bgcolor: '#ffffff',
                borderRadius: '16px',
                p: 4,
                color: '#1e293b',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                position: 'relative',
                zIndex: 1,
                border: '1px solid #e2e8f0'
              }}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 2,
                  position: 'relative'
                }}>
                  <Box sx={{
                    bgcolor: '#e6f0ff',
                    borderRadius: '50%',
                    p: 2,
                    mr: 2,
                    border: '2px solid #2563eb'
                  }}>
                    <EmailIcon sx={{ color: '#2563eb', fontSize: 28 }} />
                  </Box>
                  <Typography variant="h5" sx={{
                    fontWeight: 700,
                    color: '#1e293b'
                  }}>
                    Subscribe to Newsroom Updates
                  </Typography>
                </Box>

                <Typography variant="body1" sx={{
                  mb: 3,
                  color: '#475569',
                  lineHeight: 1.6,
                  paddingLeft: '56px'
                }}>
                  Get the latest technology insights delivered to your inbox.
                </Typography>

                <Box component="form" onSubmit={handleSubscribe}>
                  <TextField
                    fullWidth
                    placeholder="work.email@company.com"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    sx={{
                      mb: 3,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: '#f8fafc',
                        color: '#1e293b',
                        '& fieldset': {
                          borderColor: '#cbd5e1'
                        },
                        '&:hover fieldset': {
                          borderColor: '#2563eb'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#2563eb',
                          borderWidth: '2px'
                        }
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BusinessCenterIcon sx={{ color: '#64748b' }} />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                    <Button
                      variant="contained"
                      type="submit"
                      endIcon={<NotificationsIcon />}
                      sx={{
                        bgcolor: '#2563eb',
                        color: '#ffffff',
                        fontWeight: 600,
                        py: 1,
                        px: 4,
                        borderRadius: '8px',
                        width: 'auto',
                        minWidth: '200px',
                        '&:hover': {
                          bgcolor: '#1d4ed8'
                        }
                      }}
                    >
                      Subscribe
                    </Button>
                  </Box>

                  <Typography variant="caption" sx={{
                    display: 'block',
                    textAlign: 'center',
                    mt: 2,
                    color: '#64748b'
                  }}>
                    No spam. Unsubscribe anytime.
                  </Typography>
                </Box>
              </Box>
            </Grid> */}
          </Grid>
        </Container>
      </Box>
      {/* Stats Section */}
      <Box sx={{
        py: 8,
        bgcolor: colors.white
      }}>
        <Container maxWidth="xl">
          <Grid container spacing={3} justifyContent="center">
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Card sx={{
                  textAlign: 'center',
                  bgcolor: 'transparent',
                  boxShadow: 'none',
                  border: `1px solid ${alpha(colors.primary, 0.1)}`,
                  borderRadius: 3,
                  py: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    borderColor: colors.primary,
                    boxShadow: `0 10px 30px ${alpha(colors.primary, 0.1)}`
                  }
                }}>
                  <Typography variant="h3" sx={{
                    fontWeight: 900,
                    color: colors.primary,
                    mb: 1
                  }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="h6" sx={{
                    mb: 1,
                    color: colors.textDark,
                    fontWeight: 600,
                    fontSize: '0.9rem'
                  }}>
                    {stat.label}
                  </Typography>
                  <Typography variant="h5">
                    {stat.icon}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Featured Articles */}
      <Box sx={{
        py: 10,
        bgcolor: colors.white
      }}>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Typography variant="h2" sx={{
              fontWeight: 900,
              color: colors.darkBlue
            }}>
              Featured Articles
            </Typography>
           
          </Box>

          <Grid container spacing={4}>
            {articles.slice(0, 3).map((article) => (
              <Grid item xs={12} md={4} key={article.id}>
                <Card sx={{
                  height: '100%',
                  bgcolor: colors.lightGray,
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  border: `1px solid ${alpha(colors.darkGray, 0.1)}`,
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0 20px 40px ${alpha(colors.darkBlue, 0.1)}`
                  }
                }}>
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Chip
                        label={article.category}
                        size="small"
                        sx={{
                          bgcolor: alpha(colors.primary, 0.1),
                          color: colors.primary,
                          fontWeight: 600
                        }}
                      />
                      <Typography variant="caption" sx={{ color: colors.darkGray }}>
                        {article.date}
                      </Typography>
                    </Box>

                    <Typography variant="h5" sx={{
                      fontWeight: 700,
                      mb: 2,
                      color: colors.darkBlue,
                      lineHeight: 1.3
                    }}>
                      {article.title}
                    </Typography>

                    <Typography variant="body2" sx={{
                      lineHeight: 1.6,
                      mb: 3,
                      color: colors.darkGray
                    }}>
                      {article.shortContent}
                    </Typography>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
                      <Typography variant="caption" sx={{ color: colors.darkGray, display: 'flex', alignItems: 'center' }}>
                        <AccessTimeIcon sx={{ fontSize: 14, mr: 0.5 }} />
                        {article.readTime}
                      </Typography>
                      <Button
                        onClick={() => handleLearnMoreClick(article.id)}
                        endIcon={<KeyboardArrowDownIcon />}
                        sx={{
                          color: colors.primary,
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          '&:hover': {
                            bgcolor: alpha(colors.primary, 0.1)
                          }
                        }}
                      >
                        Read More
                      </Button>
                    </Box>

                    <Collapse in={expandedArticle === article.id}>
                      <Box sx={{ mt: 3, p: 2, bgcolor: alpha(colors.primary, 0.05), borderRadius: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, color: colors.primary, fontWeight: 600 }}>
                          Key Points:
                        </Typography>
                        <List dense>
                          {article.detailedContent.slice(0, 4).map((item, idx) => (
                            <ListItem key={idx} sx={{ py: 0.5 }}>
                              <ListItemText
                                primary={item}
                                primaryTypographyProps={{ fontSize: '0.85rem' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    </Collapse>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Technology Solutions Section */}
      <Box sx={{
        py: 10,
        bgcolor: colors.lightGray
      }}>
        <Container maxWidth="xl">
          <Typography variant="h2" sx={{
            textAlign: 'center',
            fontWeight: 900,
            mb: 2,
            color: colors.darkBlue
          }}>
            Lider Technology Solutions Pvt. Ltd.
          </Typography>
          <Typography variant="h5" sx={{
            textAlign: 'center',
            mb: 6,
            color: colors.darkGray,
            fontWeight: 300,
            maxWidth: '800px',
            mx: 'auto'
          }}>
            Comprehensive technology solutions designed for modern office environments
          </Typography>

          <Grid container spacing={4}>
            {articles.map((article, index) => (
              <Grid item xs={12} md={6} key={article.id}>
                <Accordion
                  expanded={expandedIndex === index}
                  onChange={() => handleAccordionChange(index)}
                  sx={{
                    mb: 2,
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: colors.white,
                    '&:before': { display: 'none' }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      bgcolor: colors.white,
                      borderLeft: `4px solid ${colors.primary}`,
                      '& .MuiAccordionSummary-expandIconWrapper': {
                        color: colors.primary
                      },
                      '&:hover': {
                        bgcolor: alpha(colors.primary, 0.05)
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Box sx={{
                        mr: 3,
                        bgcolor: alpha(colors.primary, 0.1),
                        borderRadius: 2,
                        p: 1.5
                      }}>
                        {React.cloneElement(article.icon, {
                          sx: {
                            fontSize: 24,
                            color: colors.primary
                          }
                        })}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: colors.darkBlue }}>
                            {article.title}
                          </Typography>
                          <Chip
                            label={article.category}
                            size="small"
                            sx={{
                              bgcolor: alpha(colors.secondary, 0.1),
                              color: colors.secondary,
                              fontWeight: 500
                            }}
                          />
                        </Box>
                        <Typography variant="caption" sx={{ color: colors.darkGray }}>
                          By {article.author} • {article.date}
                        </Typography>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ bgcolor: alpha(colors.primary, 0.02) }}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" sx={{ mb: 2, color: colors.darkBlue, fontWeight: 600 }}>
                          Solution Features:
                        </Typography>
                        <List dense>
                          {article.detailedContent.map((item, idx) => (
                            <ListItem key={idx} sx={{ py: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <Box sx={{
                                  width: 6,
                                  height: 6,
                                  bgcolor: colors.primary,
                                  borderRadius: '50%'
                                }} />
                              </ListItemIcon>
                              <ListItemText
                                primary={item}
                                primaryTypographyProps={{ fontSize: '0.875rem' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" sx={{ mb: 2, color: colors.darkBlue, fontWeight: 600 }}>
                          Business Impact:
                        </Typography>
                        <List dense>
                          {article.benefits.map((benefit, idx) => (
                            <ListItem key={idx} sx={{ py: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <Box sx={{
                                  width: 8,
                                  height: 8,
                                  bgcolor: colors.secondary,
                                  borderRadius: '50%'
                                }} />
                              </ListItemIcon>
                              <ListItemText
                                primary={benefit}
                                primaryTypographyProps={{
                                  fontSize: '0.875rem',
                                  sx: {
                                    color: colors.secondary,
                                    fontWeight: 500
                                  }
                                }}
                              />
                            </ListItem>
                          ))}
                        </List>
                        <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${alpha(colors.darkGray, 0.1)}` }}>
                          <Typography variant="caption" sx={{ color: colors.darkGray, display: 'block', mb: 1 }}>
                            Share this insight:
                          </Typography>
                          <Stack direction="row" spacing={1}>
                            <IconButton size="small">
                              <TwitterIcon sx={{ fontSize: 18, color: '#1DA1F2' }} />
                            </IconButton>
                            <IconButton size="small">
                              <LinkedInIcon sx={{ fontSize: 18, color: '#0077B5' }} />
                            </IconButton>
                            <IconButton size="small">
                              <FacebookIcon sx={{ fontSize: 18, color: '#4267B2' }} />
                            </IconButton>
                          </Stack>
                        </Box>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Resources Section */}
      {/* <Box sx={{
        py: 8,
        bgcolor: colors.white
      }}>
        <Container maxWidth="xl">
          <Typography variant="h2" sx={{
            textAlign: 'center',
            fontWeight: 900,
            mb: 6,
            color: colors.darkBlue
          }}>
            Downloadable Resources
          </Typography>

          <Grid container spacing={3} justifyContent="center">
            {resources.map((resource, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card sx={{
                  textAlign: 'center',
                  borderRadius: 3,
                  p: 3,
                  transition: 'all 0.3s ease',
                  border: `1px solid ${alpha(colors.primary, 0.1)}`,
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: `0 15px 35px ${alpha(colors.primary, 0.15)}`
                  }
                }}>
                  <Typography variant="h2" sx={{ mb: 2 }}>
                    {resource.icon}
                  </Typography>
                  <Typography variant="h6" sx={{
                    mb: 1,
                    color: colors.darkBlue,
                    fontWeight: 600
                  }}>
                    {resource.title}
                  </Typography>
                  <Typography variant="caption" sx={{
                    color: colors.darkGray,
                    display: 'block',
                    mb: 2
                  }}>
                    {resource.type}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<DownloadIcon />}
                    sx={{
                      color: colors.primary,
                      fontWeight: 600
                    }}
                  >
                    Download
                  </Button>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box> */}

      {/* Newsletter CTA */}
      <Box sx={{
        py: 12,
        background: `linear-gradient(135deg, ${colors.darkBlue} 0%, ${colors.primary} 100%)`,
        color: colors.white,
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            linear-gradient(45deg, transparent 65%, ${alpha(colors.accent, 0.1)} 65%),
            linear-gradient(135deg, transparent 75%, ${alpha(colors.secondary, 0.1)} 75%)
          `,
          backgroundSize: '100px 100px'
        }} />

        <Container maxWidth="xl">
          <Grid container alignItems="center" spacing={6}>
            <Grid item xs={12} md={7}>
              <Typography variant="h2" sx={{
                fontWeight: 900,
                mb: 3,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                color: colors.white
              }}>
                Be Connected with
                <Box component="span" sx={{
                  color: colors.secondary,
                  display: 'block'
                }}>
                  Tech Insights
                </Box>
              </Typography>
              {/* <Typography variant="h6" sx={{
                mb: 4,
                fontWeight: 300,
                opacity: 0.9,
                color: alpha(colors.white, 0.9)
              }}>
                Join 10,000+ technology leaders who receive our monthly insights.
              </Typography> */}

            </Grid>
            <Grid item xs={12} md={5}>
              <Box sx={{
                bgcolor: alpha(colors.white, 0.05),
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha(colors.white, 0.1)}`,
                borderRadius: 4,
                p: 5
              }}>
                <Typography variant="h5" sx={{
                  fontWeight: 700,
                  mb: 2,
                  color: colors.white
                }}>
                  Subscribe to Newsroom
                </Typography>
                <Typography variant="body1" sx={{
                  mb: 4,
                  opacity: 0.9,
                  color: alpha(colors.white, 0.9)
                }}>
                  Weekly technology insights, case studies and industry analysis.
                </Typography>
                <Box component="form" onSubmit={handleSubscribe}>
                  <TextField
                    fullWidth
                    placeholder="Your professional email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    sx={{
                      mb: 3,
                      bgcolor: alpha(colors.white, 0.1),
                      borderRadius: 1,
                      '& .MuiOutlinedInput-root': {
                        color: colors.white,
                        '& fieldset': {
                          borderColor: alpha(colors.white, 0.3)
                        },
                        '&:hover fieldset': {
                          borderColor: colors.accent
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: colors.accent
                        }
                      }
                    }}
                    InputProps={{
                      placeholder: 'Your professional email',
                      sx: { color: colors.white }
                    }}
                  />
                  <Button
                    fullWidth
                    variant="contained"
                    type="submit"
                    endIcon={<SendIcon />}
                    sx={{
                      bgcolor: colors.accent,
                      color: colors.white,
                      fontWeight: 700,
                      py: 1.5,
                      borderRadius: 1,
                      '&:hover': {
                        bgcolor: '#ff5a1f'
                      }
                    }}
                  >
                    Subscribe Now
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <HomeFooter />
    </Box>
  );
};

export default Newsroom;
